import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { EndsSoonEyebrow } from '~/features/promotions/components/ends-soon-eyebrow';
import {
  type LaborDay2026SaleVariant,
  getLaborDay2026SaleCheckoutHref,
  LABOR_DAY_2026_SALE_CTA_LABEL,
  LABOR_DAY_2026_SALE_VARIANTS,
} from '~/features/promotions/constants/labor-day-2026-sale';

/**
 * Homepage Labor Day 2026 sale card (Figma 2070:6019 desktop / 2070:6063 mobile): black background
 * with a blue gradient + grid + circular "50% OFF" badge graphic on the right, purple "Ends soon"
 * eyebrow, orange-to-purple gradient headline, gray subtitle, and solid blue "Save Now" CTA.
 * A thin wrapper around the shared `PromoCard` with variant-specific background art.
 */
export function LaborDay2026PromoCard({
  variant,
  promoCode,
  imagePriority,
  onClick,
}: {
  variant: LaborDay2026SaleVariant;
  /** Live Braavos coupon from the promotion response; appended to the CTA as `?coupon=`. */
  promoCode?: string | null;
  imagePriority?: boolean;
  onClick?: () => void;
}) {
  const config = LABOR_DAY_2026_SALE_VARIANTS[variant];
  const checkoutHref = getLaborDay2026SaleCheckoutHref(variant, promoCode);
  const cardId = `labor-day-2026-${variant}-title`;

  const desktopBackground = (
    <Image
      alt=''
      loading='eager'
      priority={imagePriority}
      sizes='(max-width: 768px) 100vw, 768px'
      src={config.homepageBg}
      style={{ objectFit: config.homepageObjectFit, objectPosition: config.homepageObjectPosition }}
      fill
    />
  );

  return (
    <PromoCard
      backgroundColor={config.backgroundColor}
      eyebrowSlot={<EndsSoonEyebrow variant='labor_day' />}
      id={cardId}
      backgroundImageSlot={
        <>
          <Box
            bottom={0}
            display={{ base: 'block', md: 'none' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            {/* CSS gradient fills the card at any aspect (the baked-in bg image is landscape and zooms on the
                portrait mobile card); the badge is a separate right-anchored asset so it stays crisp. */}
            <Box
              bottom={0}
              left={0}
              position='absolute'
              right={0}
              style={{ backgroundImage: config.homepageMobileGradient }}
              top={0}
            />
            <Box
              height={{ base: '158px', md: 'auto' }}
              pointerEvents='none'
              position='absolute'
              right={{ base: '-16px', md: 0 }}
              top='50%'
              transform='translateY(-50%)'
              width={{ base: '150px', md: 'auto' }}
            >
              <Image
                alt=''
                loading='eager'
                priority={imagePriority}
                sizes='150px'
                src={config.homepageBadgeMobile}
                style={{ objectFit: 'contain', objectPosition: 'center' }}
                fill
              />
            </Box>
          </Box>
          <Box
            bottom={0}
            display={{ base: 'none', md: 'block' }}
            left={0}
            pointerEvents='none'
            position='absolute'
            right={0}
            top={0}
            zIndex={0}
          >
            {desktopBackground}
          </Box>
        </>
      }
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            backgroundColor: config.ctaColor,
            color: 'white',
            colorScheme: 'blue',
            leftIcon: <CircleArrowRightIcon height='18px' width='18px' />,
            position: 'relative',
            size: 'icon-left',
            variant: 'solid',
            zIndex: 1,
            width: {
              base: '100%',
              md: '152px',
            },
            onClick,
          }}
        >
          {LABOR_DAY_2026_SALE_CTA_LABEL}
        </ButtonLink>
      }
      contentSlot={
        <Box marginRight={{ base: 0, md: '288px' }}>
          <Heading
            as='h2'
            fontWeight='600'
            id={cardId}
            letterSpacing='-0.01em'
            mb={3}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
            lineHeight={{
              base: '1.15',
              md: '1.1',
            }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              <Box as='span' bgClip='text' bgGradient={config.headlineAccentGradient} mr={1}>
                {config.homepageHeadlineLine1}
              </Box>
              <Box as='span' color='white'>
                {config.homepageHeadlineLine2}
              </Box>
              {/* Third segment wraps to its own line on mobile (Figma 2070:6063), inline on desktop. */}
              <Box as='span' color='white' display={{ base: 'block', md: 'inline' }}>
                {` ${config.homepageHeadlineLine3}`}
              </Box>
            </LinkOverlay>
          </Heading>
          <Text
            color={config.subtitleColor}
            fontSize={{ base: '0.875rem', md: 'md' }}
            lineHeight={{ base: 1.4, md: 1.5 }}
            marginRight={{ base: '130px', md: 0 }}
          >
            {config.homepageSubtitle}
            {/* Second segment breaks to its own line on mobile (Figma explicit <br>), inline on desktop. */}
            <Box as='span' display={{ base: 'block', md: 'inline' }}>
              {` ${config.homepageSubtitleLine2}`}
            </Box>
          </Text>
        </Box>
      }
      padding={{
        base: '32px',
        md: '48px 48px 56px 48px',
      }}
    />
  );
}
